import { useTranslation } from 'react-i18next';
import { Apple, Check, Moon, Plus, Star, Sunrise, Utensils } from 'lucide-react';

import { formatQuantity, numberFormatter, sumEntries } from '../../utils/calorieLog';

// Meal names come from the AI plan, so the icon follows the name
const mealIcon = (meal) => {
  if (meal.isFreeMeal) return Star;
  const name = meal.name?.toLowerCase() ?? '';
  if (/caf[eé] da manh|breakfast|desjejum/.test(name)) return Sunrise;
  if (/jantar|dinner|ceia|supper/.test(name)) return Moon;
  if (/lanche|snack|cola[cç][aã]o/.test(name)) return Apple;
  return Utensils;
};

/**
 * Today's next meal (the first with nothing logged), the one card that lifts: its planned foods and the planned
 * meal in one tap, or the free meal's budget and a way to log what was eaten.
 */
export const NextMealCard = ({ meal, mealIndex, onLogPlanned, onAdd, isSaving, isLoggingPlanned }) => {
  const { t, i18n: { language } } = useTranslation();
  const ui = (key, options) => t(`pages.CalorieTracker.ui.${key}`, options);
  const formatNumber = numberFormatter(language);
  const Icon = mealIcon(meal);
  const plannedFoods = (meal.foods || []).filter((food) => food.name);
  const plannedKcal = sumEntries(plannedFoods).calories;

  let meta;
  if (meal.isFreeMeal) meta = meal.budget ? ui('freeMealMeta', { kcal: formatNumber(meal.budget) }) : ui('freeMealShort');
  else meta = ui('plannedMeta', { kcal: formatNumber(plannedKcal), count: plannedFoods.length });

  return (
    <section className={`mt-cal-next${meal.isFreeMeal ? ' mt-cal-next--free' : ''}`} aria-label={ui('nextMeal')}>
      <div className="mt-cal-next__head">
        <span className="mt-cal-next__disc" aria-hidden="true">
          <Icon />
        </span>
        <div>
          <h2 className="mt-cal-next__title">
            {meal.name}
            <span className="mt-cal-pill">{ui('next')}</span>
          </h2>
          <p className="mt-cal-next__meta">{meta}</p>
        </div>
      </div>

      {meal.isFreeMeal ? (
        <p className="mt-cal-next__free">{meal.budget ? ui('freeMealText', { kcal: formatNumber(meal.budget) }) : ui('freeMealTextNoBudget')}</p>
      ) : (
        plannedFoods.length > 0 && (
          <ul className="mt-cal-next__foods">
            {plannedFoods.map((food, index) => (
              <li key={`${index}-${food.name}`}>
                <span>
                  {food.name} {food.quantity && <em>{formatQuantity(food.quantity)}</em>}
                </span>
                <b>{ui('kcal', { kcal: formatNumber(food.calories) })}</b>
              </li>
            ))}
          </ul>
        )
      )}

      <div className="mt-cal-next__actions">
        {plannedFoods.length > 0 ? (
          <>
            <button
              type="button"
              className="mt-btn mt-btn--primary mt-cal-btn-big"
              onClick={() => onLogPlanned(meal, mealIndex)}
              disabled={isSaving}
              aria-busy={isLoggingPlanned || undefined}
            >
              {isLoggingPlanned ? <span className="mt-btn__spinner" aria-hidden="true" /> : <Check aria-hidden="true" />}
              {ui('logPlanned', { kcal: formatNumber(plannedKcal) })}
            </button>
            <button type="button" className="mt-btn mt-btn--quiet mt-cal-btn" onClick={() => onAdd(mealIndex)}>
              {ui('ateSomethingElse')}
            </button>
          </>
        ) : (
          <button type="button" className="mt-btn mt-btn--primary mt-cal-btn-big" onClick={() => onAdd(mealIndex, 'search')}>
            <Plus aria-hidden="true" />
            {meal.isFreeMeal ? ui('logFree') : t('pages.CalorieTracker.addFood')}
          </button>
        )}
      </div>
    </section>
  );
};
